import { DateTime } from "luxon";
import { z } from "zod/v4";
import { IanaTimezone } from "./iana-timezone";

/*
 * A local date-time (`YYYY-MM-DDTHH:mm`) is a reading of a zone's wall clock, and around a clock
 * change that reading is not one instant: when the clocks go forward a reading never happens, and
 * when they go back it happens twice. luxon resolves both to a single instant (shifting a skipped
 * reading forward, and taking the first of a repeated one), which is right for "when is 09:00" but
 * wrong for the edges of a range — it drops the half-hour after a skipped 02:30, or the second
 * hour after a repeated one.
 *
 * These two bounds instead answer for a *range*: a span of local time from A up to B is the
 * instants [localStartBound(A), localEndBound(B)), which is the widest reading of it. Nothing whose
 * wall clock reads within [A, B) falls outside, on any day.
 */

const DAY_MS = 86_400_000;

/**
 * A wall-clock reading, `YYYY-MM-DDTHH:mm`, with no offset or zone of its own — the zone is always
 * passed alongside, and an offset in the string would silently override it. Seconds, `24:00` and
 * days that do not exist are refused rather than rolled over.
 */
export const LocalDateTime = z.iso
  .datetime({ local: true, precision: -1 })
  .refine((text) => !text.endsWith("Z"), { message: "A local date-time carries no UTC designator" });

/** The wall clock as if it were UTC, so readings compare as plain numbers. */
function readingOf(localDateTime: string): number {
  const text = LocalDateTime.parse(localDateTime);
  return DateTime.fromISO(text, { zone: "UTC" }).toMillis();
}

/*
 * Checked against the IANA list rather than left to luxon, which would also accept "local" — the
 * host's own zone — and fixed offsets such as "UTC+5".
 */
function zoneOf(timezone: string): string {
  return IanaTimezone.parse(timezone);
}

function offsetMs(at: number, timezone: string): number {
  return DateTime.fromMillis(at, { zone: timezone }).offset * 60_000;
}

/** What the wall clock in `timezone` reads at `at`, as if it were UTC. */
const clockAt = (at: number, timezone: string) => at + offsetMs(at, timezone);

/**
 * The instants at which `timezone`'s clock reads `reading` — none, one or two — or, when the clocks
 * skip it, the instant they jump past it.
 *
 * A reading can only map to an instant through an offset in force near it, so the candidates are
 * the reading shifted by the offsets a day either side; no zone changes its clocks twice within a
 * day. When neither candidate reads true, the reading is in a gap, and the jump lies between them.
 */
function resolve(reading: number, timezone: string): { readonly first: number; readonly last: number } {
  const candidates = [
    reading - offsetMs(reading - DAY_MS, timezone),
    reading - offsetMs(reading + DAY_MS, timezone),
  ].sort((a, b) => a - b);
  const exact = candidates.filter((at) => clockAt(at, timezone) === reading);
  // The end is the last time the clock *arrives* at the reading from earlier. Where a repeated hour
  // begins, the second occurrence is reached from later (02:59 back to 02:00), so the clock has not
  // read earlier than the reading since the first, and the first is the end.
  const arrivals = exact.filter((at) => clockAt(at - 1, timezone) < reading);
  const first = exact[0];
  const last = arrivals[arrivals.length - 1] ?? first;
  if (first !== undefined && last !== undefined) return { first, last };

  // In a gap the clock is behind the reading at the earlier candidate and past it at the later
  // one, with the jump between; binary search finds the first millisecond that reads past it.
  let [low = 0, high = 0] = candidates;
  while (high - low > 1) {
    const middle = Math.floor((low + high) / 2);
    if (clockAt(middle, timezone) >= reading) high = middle;
    else low = middle;
  }
  return { first: high, last: high };
}

/**
 * The first instant `timezone`'s clock reads `localDateTime` or later — the start of a range of
 * local time beginning there.
 *
 * Its first occurrence when the clocks go back; the moment they jump when they go forward past it.
 * Throws on a reading that is not a {@link LocalDateTime}, or a zone that is not an IANA zone.
 */
export function localStartBound(localDateTime: string, timezone: string): Date {
  return new Date(resolve(readingOf(localDateTime), zoneOf(timezone)).first);
}

/**
 * The instant from which `timezone`'s clock never again reads earlier than `localDateTime` — the
 * exclusive end of a range of local time ending there.
 *
 * Its second occurrence when the clocks go back — unless it is where the repeated hour begins, which
 * is reached from later; then its first. The moment the clocks jump when they go forward past it.
 * Throws on a reading that is not a {@link LocalDateTime}, or a zone that is not an IANA zone.
 */
export function localEndBound(localDateTime: string, timezone: string): Date {
  return new Date(resolve(readingOf(localDateTime), zoneOf(timezone)).last);
}
